import { createRouter, createWebHistory } from 'vue-router'
import { auth } from "@/lib/services/auth.service.js";
import store from '@/lib/store';
import { workboardContext } from '@/lib/workboardContext';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '',
      name: 'home',
      component: () => import('@/views/index.vue'),
      redirect: `/home`,
      children: [
        {
          // Guest surface: research/search works signed out (busy.me is the landing page now);
          // signing in is required only at the first save (see research store save actions).
          path: "home",
          component: () => import("@/views/Home.vue"),
          meta: { public: true },
        },
        {
          // Guest surface, same as `home` above: the SmartBox already pushes a guest straight here
          // the moment a story is created (SmartBox.vue's `router.push(\`/ongoing/${id}\`)`), and
          // Home's new unfold-then-open journey (busy-my-web#336) does the exact same push from a
          // SECOND, fast click. Without `public`, this route's own entry in the guard below forces
          // a FRESH `/bff/user` round trip and, on anything short of a confirmed 200, a full-page
          // redirect to the IdP — dropping the SPA (and the Follow-ups state the user was just
          // looking at) rather than opening the story (busy-my-web#353, Saffet: a story row bounced
          // back instead of opening). `home`'s own probe is fire-and-forget, so a click this fast
          // can beat it. Public here closes that gap the same way `home` already closes it: the
          // page renders from whatever the store already knows (guest, cached, or signed-in), and a
          // truly-expired session is still caught per-request by the 401 guard (busy-my-web#321) —
          // no navigation guard has to get there first.
          path: "ongoing/:id",
          props: true,
          component: () => import("@/views/StoryView.vue"),
          meta: { public: true },
        },
        // {
        //   path: "overview",
        //   component: () => import("@/views/overview/Overview"),
        // },
        {
          path: "emails",
          redirect: `/emails/inbox`,
          children: [
            {
              path: "inbox",
              component: () => import("@/views/emails/Inbox.vue"),
            },
            {
              // The Email Workspace (busy-my-web#16): the Briefing, the sender
              // groups, and bulk archive. Reached from the Home follow-up
              // "Emails need attention" (step 5, not built yet) and directly.
              path: "workspace",
              component: () => import("@/views/emails/EmailWorkspace.vue"),
              meta: { title: "Email Workspace — Busy.me" },
            },
            // The three commerce lists (busy-ai-srv#15, spec Appendix §7/§8/§9), reached from the
            // Email Briefing's Orders / Receipts / Subscriptions entries — and, for receipts,
            // from a Story with ?ongoingId= ("Receipts for <Ongoing>").
            {
              path: "orders",
              component: () => import("@/views/emails/Orders.vue"),
              meta: { title: "Orders — Busy.me" },
            },
            {
              path: "receipts",
              component: () => import("@/views/emails/Receipts.vue"),
              meta: { title: "Receipts — Busy.me" },
            },
            {
              path: "subscriptions",
              component: () => import("@/views/emails/Subscriptions.vue"),
              meta: { title: "Subscriptions — Busy.me" },
            },
            {
              path: "timeline/:id",
              props: true,
              component: () => import("@/views/emails/TimelineView.vue"),
            },
            {
              path: "inbox/:id",
              props: true,
              component: () => import("@/views/emails/ContactFilterInbox.vue"),
            },
            {
              path: ":id/reply",
              props: true,
            },
            // {
            //   path: "new",
            //   props: true,
            //   component: () => import("@/views/emails/NewMail.vue"),

            // },
            {
              path: "waitingOn",
              component: () => import("@/views/emails/Inbox.vue"),
            },
            {
              path: "important",
              component: () => import("@/views/emails/Inbox.vue"),
            },
            {
              path: "overdue",
              component: () => import("@/views/emails/Inbox.vue"),
            },
            {
              path: "dueSoon",
              component: () => import("@/views/emails/Inbox.vue"),
            },
            {
              path: "archived",
              component: () => import("@/views/emails/Inbox.vue"),
            },
            {
              path: "draft",
              component: () => import("@/views/emails/Draft.vue"),
            },
            {
              path: "sent",
              component: () => import("@/views/emails/Inbox.vue"),
            },
            {
              path: "black-hole",
              component: () => import("@/views/emails/Inbox.vue"),
            },
            {
              path: "bin",
              component: () => import("@/views/emails/Inbox.vue"),
            },
            {
              path: "spam",
              component: () => import("@/views/emails/Spam.vue"),
            },
            // Mail waiting on the clock (busy-my-web#308) — listed and cancelled through the
            // BFF's `email/scheduled` routes (busy-main-bff#14).
            {
              path: "scheduled",
              component: () => import("@/views/emails/Scheduled.vue"),
              meta: { title: "Scheduled — Busy.me" },
            },
            // ONE of them, opened as an email (busy-my-web#442, Fatemeh: "it should open as an
            // email and the scheduling message under it, that's all"). A queued send has no
            // timeline, so it cannot be read at `/emails/timeline/:id` — this is its reader.
            {
              path: "scheduled/:id",
              props: true,
              component: () => import("@/views/emails/ScheduledMessage.vue"),
              meta: { title: "Scheduled — Busy.me" },
            },
          ]
        },
        {
          path: "tasks",
          redirect: `/tasks/all`,

          children: [
            {
              path: "today",
              component: () => import("@/views/tasks/Tasks.vue"),
            },
            {
              path: "waitingOn",
              component: () => import("@/views/tasks/Tasks.vue"),
            },
            {
              path: "important",
              component: () => import("@/views/tasks/Tasks.vue"),
            },
            {
              path: "completed",
              component: () => import("@/views/tasks/Tasks.vue"),
            },
            {
              path: "all",
              component: () => import("@/views/tasks/Tasks.vue"),
            },
            {
              path: "next7days",
              component: () => import("@/views/tasks/Tasks.vue"),
            },
            {
              path: "next30days",
              component: () => import("@/views/tasks/Tasks.vue"),
            },
            {
              path: "someday",
              component: () => import("@/views/tasks/Tasks.vue"),
            },
            {
              path: "bin",
              component: () => import("@/views/tasks/Tasks.vue"),
            },
            // ONE task, opened as a page (Pouria, 2026-10-02: "when we click on task it should open
            // in a single page and show attachment as well, follow same principle as email"). The
            // Tasks listings' rows push here with `?from=<listing>` so Back returns to that listing
            // (lib/taskReader.js).
            {
              path: "item/:id",
              props: true,
              component: () => import("@/views/tasks/TaskReader.vue"),
              meta: { title: "Task — Busy.me" },
            },

          ]
        },
        {
          path: "events",
          redirect: `/events/calendar`,
          children: [
            {
              path: "all-events",
              component: () => import("@/views/events/Events.vue"),
            },
            {
              path: "important",
              component: () => import("@/views/events/Events.vue"),
            },
            {
              path: "recurring",
              component: () => import("@/views/events/Events.vue"),
            },
            {
              path: "past",
              component: () => import("@/views/events/Events.vue"),
            },
            {
              path: "calendar",
              component: () => import("@/views/events/Calendar.vue"),
            },
            // ONE event, opened as a page (Pouria, 2026-10-02: "do it for notes and events as well").
            // The Events listings' rows push here with `?from=<listing>` so Back returns there
            // (lib/entityReader.js). The calendar grid keeps its own click.
            {
              path: "item/:id",
              props: (route) => ({ id: String(route.params.id), kind: 'events' }),
              component: () => import("@/views/entities/EntityReader.vue"),
              meta: { title: "Event — Busy.me" },
            },
          ]
        },
        {
          path: "notes",
          redirect: `/notes/all-notes`,
          children: [
            {
              path: "all-notes",
              component: () => import("@/views/notes/Notes.vue"),
            },
            {
              path: "important",
              component: () => import("@/views/notes/Notes.vue"),
            },
            {
              path: "preset",
              component: () => import("@/views/notes/Notes.vue"),
            },
            {
              path: "bin",
              component: () => import("@/views/notes/Notes.vue"),
            },
            // ONE note, opened as a page (Pouria, 2026-10-02: "do it for notes and events as well").
            // The Notes listings' rows push here with `?from=<listing>` (lib/entityReader.js).
            {
              path: "item/:id",
              props: (route) => ({ id: String(route.params.id), kind: 'notes' }),
              component: () => import("@/views/entities/EntityReader.vue"),
              meta: { title: "Note — Busy.me" },
            },
          ]
        },
        {
          path: "contacts",
          redirect: `/contacts/all-contacts`,
          children: [
            {
              path: "all-contacts",
              component: () => import("@/views/contacts/Contacts.vue"),
            }
          ]
        },
        {
          path: "settings",
          children: [
            {
              path: "rebase",
              component: () => import("@/views/settings/RebaseSync.vue"),
            },
            {
              path: "rebase-to-google",
              component: () => import("@/views/settings/RebaseToGoogle.vue"),
            },
            {
              path: "ai",
              component: () => import("@/views/settings/AiSettings.vue"),
            },
            {
              // AI usage against the plan's monthly allowance (busy-intelligent-srv#19).
              path: "usage",
              component: () => import("@/views/settings/UsageSettings.vue"),
            },
            {
              path: "appearance",
              component: () => import("@/views/settings/AppearanceSettings.vue"),
            },
            {
              path: "account",
              component: () => import("@/views/settings/AccountSettings.vue"),
            },
            {
              path: "integrations",
              component: () => import("@/views/settings/Integrations.vue"),
            },
          ]
        },
        {
          // FILES IS NOT DESIGNED YET (2026-09-15 parity audit A-11, busy-my-web#461). The module
          // is a "Coming soon" card on the Integrations → Modules tab (lib/integrationsCatalog's
          // MODULES) and it is commented out of the launcher and the sidebar menu — but the six
          // `/files/*` URLs still resolved to `views/files/Files.vue`, a local-disk explorer from
          // the old shell that no frame describes. Typing one (or following the attachments flow's
          // "Organize in Files" / the box's "See all") landed on an un-designed page.
          //
          // Until a Files design exists, every one of them redirects Home — where the box is, and
          // where the Coming-soon card is one click away. The Files.vue view and its menu constants
          // are KEPT so the page can come back with its design rather than be rebuilt.
          path: "files/:pathMatch(.*)*",
          redirect: '/home',
        },
        {
          path: "files",
          redirect: '/home',
        },
      ]

    },
    {
      // Stripe sends the browser back here after checkout (see payment.js successUrl/cancelUrl).
      // Standalone full-screen pages — deliberately outside the app shell.
      path: '/payment-success',
      component: () => import('@/views/payment/PaymentSuccess.vue'),
      meta: { title: 'Payment Successful — Busy.me' },
    },
    {
      path: '/payment-cancel',
      component: () => import('@/views/payment/PaymentCancel.vue'),
      meta: { title: 'Payment Cancelled — Busy.me' },
    },
    {
      // Public pages (busy.me/hub/*). meta.public skips the auth guard: Google's OAuth verification
      // requires these pages readable without signing in.
      path: '/hub',
      meta: { public: true },
      children: [
        { path: '', redirect: '/hub/landing' },
        { path: 'landing', component: () => import('@/views/hub/Landing.vue'), meta: { public: true, title: 'Busy.me — Email, Tasks & Research' } },
        { path: 'privacy', component: () => import('@/views/hub/PrivacyPolicy.vue'), meta: { public: true, title: 'Privacy Policy — Busy.me' } },
        { path: 'terms', component: () => import('@/views/hub/Terms.vue'), meta: { public: true, title: 'Terms of Service — Busy.me' } },
      ]
    },
    {
      path: '/bff/:pathMatch(.*)*',
      redirect: '/'
    },
    {
    }
  ]
})

export default router;

router.beforeEach(async (to, from) => {
  const baseUri = window.location.origin

  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find(r => r.meta && r.meta.title);

  const nearestWithMeta = to.matched
    .slice()
    .reverse()
    .find(r => r.meta && r.meta.metaTags);
  const previousNearestWithMeta = from.matched
    .slice()
    .reverse()
    .find(r => r.meta && r.meta.metaTags);
  if (nearestWithTitle) {
    document.title = nearestWithTitle.meta.title;
  } else {
    // ← here ↓
    if (previousNearestWithMeta) {
      document.title = previousNearestWithMeta.meta.title;
    }
  }
  Array.from(
    document.querySelectorAll("[data-vue-router-controlled]")
  ).map(el => el.parentNode.removeChild(el));




  // Public routes (/hub/*, /home) render without a session — no login redirect. A returning
  // user's session is still loaded silently so the page shows their data when they have one.
  if (to.matched.some(record => record.meta.public)) {
    if (!store.getters["user/isAuthenticated"]) {
      try {
        const user = await auth.getUser();
        if (user) store.dispatch("user/fetchUser", user);
      } catch { /* stay a guest */ }
    }
    return;
  }

  let isAuthenticated = store.getters["user/isAuthenticated"];
  if (!isAuthenticated) {
    sessionStorage.setItem("redirectedUri", baseUri + to.fullPath);
    await auth.authenticate(to.fullPath);
    isAuthenticated = store.getters["user/isAuthenticated"];
    if (!isAuthenticated) {
      return false;
    }
    // The full-mailbox sync poll used to be dispatched here too (commented out in 7977cfed). It
    // lives in one place now — auth.service.js loadUserToStore, which runs on this path as well —
    // so a Pro user gets the banner back on boot without two routes racing to start it
    // (busy-my-web#502).
  }

});

// Stale-deploy resilience: a lazily-loaded route chunk 404s after a new deploy (its content hash
// changed), the server serves index.html in its place, and the browser reports a ChunkLoadError /
// "Unexpected token '<'" — leaving a blank page until a manual hard refresh. Reload ONCE to pick up
// the fresh index.html + current chunks. A sessionStorage guard prevents an infinite reload loop.
const CHUNK_RELOAD_KEY = 'busy.chunkReload';
router.onError((error, to) => {
  const name = error?.name || '';
  const msg = error?.message || '';
  const isChunkError =
    /ChunkLoadError/i.test(name) ||
    /Loading (CSS )?chunk [^\s]+ failed/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Unexpected token '</.test(msg);
  if (!isChunkError) return;

  const target = to?.fullPath || window.location.href;
  // If we've already tried reloading for this exact target, the assets really are missing — stop,
  // so we don't loop forever.
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === target) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, target);
  if (to?.fullPath) window.location.assign(to.fullPath);
  else window.location.reload();
});
// A completed navigation means fresh assets loaded — clear the guard so a future stale deploy can
// trigger the one-shot reload again.
router.afterEach(() => {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
});

// The Work-board follows the page (busy-my-web#84). Every completed navigation tells the board
// which context we are in; the board keeps itself while that context is the one it was filled in,
// and empties on the way out (a different ongoing, home, a module page). AFTER the navigation, not
// before: a guard that clears on a navigation which then fails would throw the selection away
// without ever leaving the page. Query params are not part of a context, so the pack / compare /
// email overlays on `/ongoing/:id` keep everything.
router.afterEach((to) => {
  // …and while the email session is open (busy-my-web#99) the board belongs to THAT, not to the
  // page underneath it: the workspace opens in place, so a navigation inside it must not look like
  // leaving it.
  // …and the reply composer (`?compose=reply`, busy-ai-srv#13) is a context of its own, so the query
  // comes along: it is the one param that names a surface rather than decorating a page.
  const ctx = workboardContext(to.path, {
    emailSession: !!store.getters['emailSession/active'],
    query: to.query,
  });
  store.dispatch('workboard/enterContext', ctx);
  // The Briefing's SUBJECT is scoped by the very same context (busy-my-web#122): a pack briefed by
  // a save has no `?pack=` behind it, so the view that clears packs on navigation never sees it go
  // — it took the pack's briefing to home and to every module page instead. Told after the board,
  // with the same string, so the strip and the board can never disagree about where we are.
  store.dispatch('briefing/enterContext', ctx);
});